import { useMemo } from "react"

import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"

import { Brain, Briefcase, CheckCircle2, Lock, Play } from "@/components/icons"
import { useRoutes } from "@/contexts/use-routes"
import { ProgressRing } from "@/features/training/components/progress-ring"
import { useGetTrainingModules } from "@/features/training/hooks/use-get-training-modules"
import { useGetTrainingProgress } from "@/features/training/hooks/use-get-training-progress"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"

const TRACK_ICONS: Record<string, typeof Brain> = {
  "ai-awareness": Brain,
  "everyday-ai": Briefcase,
}

export function TrainingHomePage() {
  const { t } = useTranslation("", { keyPrefix: "training" })
  const navigate = useNavigate()
  const location = useLocation()
  const { trainingTracks, trainingModules } = useRoutes()
  const progressQuery = useGetTrainingProgress()
  const modulesQuery = useGetTrainingModules()

  const trackProgress = progressQuery.data?.tracks ?? []
  const userModules = modulesQuery.data?.modules ?? []

  const settingsService = SettingsService.getInstance()
  const fullName = settingsService.claims.name
  const firstName = fullName?.split(" ")[0]

  // Build a map of trackId -> ordered sections for navigation
  const trackSectionsMap = useMemo(() => {
    const map: Record<string, { id: string; url: string }[]> = {}
    for (const track of trainingTracks) {
      const sections: { id: string; url: string }[] = []
      for (const moduleId of track.module_ids) {
        const mod = trainingModules.find((m) => m.id === moduleId)
        if (mod) {
          for (const sec of mod.sections) {
            sections.push({ id: sec.id, url: sec.url })
          }
        }
      }
      map[track.id] = sections
    }
    return map
  }, [trainingTracks, trainingModules])

  function getResumeUrl(trackId: string): string {
    const sections = trackSectionsMap[trackId] ?? []
    if (sections.length === 0) return "/training"

    for (const section of sections) {
      const status = userModules.find((m) => m.module_id === section.id)?.status
      if (!status || status !== "completed") {
        return section.url
      }
    }
    return sections[0].url
  }

  return (
    <section className="flex w-full flex-col items-center gap-10 py-8">
      <h1 className="text-foreground text-4xl font-semibold tracking-tight">
        {t("landing.title", "AI Training")}
      </h1>

      {/* Personalized greeting */}
      <div className="text-center">
        <p className="text-2xl font-bold tracking-tight md:text-3xl">
          {firstName
            ? t("landing.greeting_personal", { name: firstName })
            : t("landing.greeting_generic", "Welcome!")}
        </p>
        <p className="text-muted-foreground mt-1 text-base">
          {t("landing.greeting_subtitle", "Pick up where you left off.")}
        </p>
      </div>

      {/* Track cards */}
      <div className="grid w-full max-w-3xl grid-cols-1 gap-6 md:grid-cols-2">
        {trainingTracks.map((track) => {
          const progress = trackProgress.find((tp) => tp.trackId === track.id)
          const isLocked = progress?.locked ?? track.requires_track !== null
          const isCompleted = progress?.userCompleted ?? false
          const Icon = TRACK_ICONS[track.id] ?? Brain

          return (
            <TrackCard
              key={track.id}
              completed={isCompleted}
              icon={Icon}
              locked={isLocked}
              subtitle={t(`tracks.${track.id}.subtitle`, track.subtitle)}
              title={t(`tracks.${track.id}.title`, track.display_name)}
              userPct={progress?.userProgressPct ?? 0}
              onNavigate={() => navigate(`${getResumeUrl(track.id)}${location.search}`)}
            />
          )
        })}
      </div>

      {/* Progress rings */}
      {trackProgress.length > 0 && (
        <div className="flex w-full max-w-3xl flex-col items-center gap-4">
          <h2 className="text-lg font-semibold">{t("landing.your_progress", "Your Progress")}</h2>
          <p className="text-muted-foreground -mt-2 text-sm">
            {t(
              "landing.progress_subtitle",
              "Your progress compared to others who have started the training"
            )}
          </p>
          <div className="flex flex-col items-center gap-8 sm:flex-row sm:justify-center">
            {trainingTracks.map((track) => {
              const progress = trackProgress.find((tp) => tp.trackId === track.id)
              return (
                <ProgressRing
                  key={track.id}
                  avgPercent={progress?.platformAvgPct ?? 0}
                  completed={progress?.userCompleted}
                  label={t(`tracks.${track.id}.title`, track.display_name)}
                  percent={progress?.userProgressPct ?? 0}
                />
              )
            })}
          </div>
        </div>
      )}
    </section>
  )
}

type TrackCardProps = {
  title: string
  subtitle: string
  icon: typeof Brain
  locked: boolean
  completed: boolean
  userPct: number
  onNavigate: () => void
}

function TrackCard({
  title,
  subtitle,
  icon: Icon,
  locked,
  completed,
  userPct,
  onNavigate,
}: TrackCardProps) {
  const { t } = useTranslation("", { keyPrefix: "training.landing" })
  const hasStarted = userPct > 0

  return (
    <button
      disabled={locked}
      type="button"
      className={cn(
        "group flex w-full flex-col items-center gap-4 rounded-lg border p-8",
        "bg-card text-card-foreground shadow-sm",
        "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
        "transition-[box-shadow,translate,scale] duration-200",
        locked
          ? "cursor-not-allowed opacity-60"
          : "hover:-translate-y-1 hover:shadow-lg active:scale-[0.98]",
        completed && "border-primary/30 bg-primary/5"
      )}
      onClick={onNavigate}
    >
      {/* Icon */}
      <div
        className={cn(
          "flex h-16 w-16 items-center justify-center rounded-lg transition-colors duration-200",
          locked && "bg-muted text-muted-foreground",
          !locked && completed && "bg-primary/10 text-primary",
          !locked && !completed && "bg-primary/10 text-primary group-hover:bg-primary/20"
        )}
      >
        {locked ? <Lock className="h-8 w-8" /> : <Icon className="h-8 w-8" />}
      </div>

      {/* Text */}
      <div className="text-center">
        <p className="text-xl font-semibold">{title}</p>
        <p className="text-muted-foreground mt-1 text-sm">{subtitle}</p>
      </div>

      {/* Status */}
      <div className="mt-2">
        {locked && (
          <span className="text-muted-foreground text-xs">
            {t("locked", "Complete AI Awareness to unlock")}
          </span>
        )}
        {!locked && completed && (
          <span className="text-primary inline-flex items-center gap-1.5 text-sm font-medium">
            <CheckCircle2 className="h-4 w-4" />
            {t("completed", "Completed")}
          </span>
        )}
        {!locked && !completed && (
          <span className="bg-primary/10 text-primary inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium">
            <Play className="h-3.5 w-3.5" />
            {hasStarted ? t("continue", "Continue") : t("start", "Start")}
          </span>
        )}
      </div>
    </button>
  )
}
